import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import Filters from 'components/shared/filters';
import GridTable from 'components/shared/gridTable';
import PageContainer from 'components/shared/pageContainer';
import Search from 'components/shared/search';
import NotificationCard from 'components/shared/notificationCard';
import { sticky } from 'constants/helperCssRules';
import {
  APP_PAGES,
  PARAMS,
  SUCCESSION_KEY_JOBS_DEFAULT_PARAMS,
} from 'constants/pages';
import { successionKeyJobsSelector } from 'store/selectors/successionKeyJobsSelector';
import { successionSettingsSelector } from 'store/selectors/successionSettingsSelector';
import {
  clearSuccessionKeyJobs,
  getSuccessionKeyJobs,
} from 'store/modules/successionKeyJobs';
import {
  getSuccessionJobFamilies,
  getSuccessionCriticality,
  clearSuccessionSettings,
} from 'store/modules/successionSettings';
import { isArray, isArrayEmpty, trimString } from 'utility/helpers';
import {
  hasNextPage,
  parseDuplicateParameters,
  hasSelectedFiltersCheck,
} from 'utility/uiUtils';
import { useTranslations } from 'utility/useTranslations';
import { prepareSuccessionNavigationItems } from 'utility/succession';
import {
  INITIAL_KEY_JOB_FILTERS,
  prepareFiltersFromLocation,
  prepareKeyJobFilters,
  prepareKeyJobTableHeaders,
  prepareKeyJobTableRows,
} from './config';

const { SEARCH, PAGE } = PARAMS;
const { page: defaultPage } = SUCCESSION_KEY_JOBS_DEFAULT_PARAMS;

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  stickyHeader: {
    padding: spacing(2, 0, 4, 0),
    ...sticky(primary.white, 170),
  },
  description: {
    marginBottom: spacing(6),
  },
  jobsLink: {
    display: 'inline',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(2),
  },
  search: {
    width: 300,
    flexShrink: 0,
  },
  loader: {
    position: 'relative',
    minHeight: 240,
  },
  empty: {
    marginTop: spacing(4),
  },
  rowName: {
    color: primary.blue1,
    fontFamily: 'ProximaNova-Bold',
  },
  table: {
    '& .grid-table-header': {
      ...sticky(primary.bluish8, 276),
    },
    '& .actions-cell': {
      overflow: 'visible',
    },
  },
}));

const SuccessionKeyJobsPage = ({
  location,
  navigate,
  dispatch,
  accessControl,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SUCCESSION_KEY_JOBS);

  const keyJobs = useSelector(successionKeyJobsSelector);
  const settings = useSelector(successionSettingsSelector);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(defaultPage);
  const [search, setSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(
    INITIAL_KEY_JOB_FILTERS
  );

  const navigationItems = useMemo(
    () => prepareSuccessionNavigationItems(translations.navigationItems),
    [translations.navigationItems]
  );

  const filterLabels = useMemo(
    () => ({
      all: translations.all,
      coverage: translations.filters.labels.coverage,
      type: translations.filters.labels.type,
      family: translations.filters.labels.family,
      criticality: translations.filters.labels.criticality,
      covered: translations.coverage.covered,
      partial: translations.coverage.partial,
      gap: translations.coverage.gap,
      seat: translations.types.seat,
      generic: translations.types.generic,
    }),
    [translations]
  );

  const filters = useMemo(
    () => prepareKeyJobFilters(filterLabels, settings),
    [filterLabels, settings]
  );

  const handlePageChange = useCallback(
    async ({
      newPage = page,
      newSearch = search,
      newFilters = selectedFilters,
      isLoadMore,
    } = {}) => {
      const params = {
        [PAGE]: newPage,
        ...newFilters,
        ...(newSearch ? { [SEARCH]: newSearch } : {}),
      };
      const query = parseDuplicateParameters(params);

      if (location.search !== `?${query}`) {
        navigate(`/succession/key-jobs/?${query}`, { replace: true });
      }

      return getSuccessionKeyJobs(dispatch, isLoadMore, params);
    },
    [dispatch, location.search, navigate, page, search, selectedFilters]
  );

  const onSearch = async value => {
    const nextSearch = trimString(value);

    try {
      setPage(defaultPage);
      setSearch(nextSearch);
      setIsLoading(true);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: nextSearch,
        newFilters: selectedFilters,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onApplyFilters = async currentFilters => {
    try {
      setPage(defaultPage);
      setSelectedFilters(currentFilters);
      setIsLoading(true);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newFilters: currentFilters,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onLoadMore = () => {
    setPage(page + 1);
    handlePageChange({
      newPage: page + 1,
      newSearch: search,
      newFilters: selectedFilters,
      isLoadMore: true,
    });
  };

  const headers = useMemo(
    () => prepareKeyJobTableHeaders(translations.columns),
    [translations.columns]
  );

  const rows = useMemo(
    () => prepareKeyJobTableRows(keyJobs.results, translations),
    [keyJobs.results, translations]
  );

  const hasRows = isArray(rows) && !isArrayEmpty(rows);
  const hasLoadMore = hasNextPage(keyJobs);
  const isFiltered =
    Boolean(search) || hasSelectedFiltersCheck(selectedFilters);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const initial = prepareFiltersFromLocation(location.search);
      setPage(defaultPage);
      setSearch(initial.search);
      setSelectedFilters(initial.filters);
      try {
        await Promise.all([
          getSuccessionJobFamilies(dispatch),
          getSuccessionCriticality(dispatch),
          handlePageChange({
            newPage: defaultPage,
            newSearch: initial.search,
            newFilters: initial.filters,
          }),
        ]);
      } finally {
        if (active) setIsInitialLoading(false);
      }
    };

    load();
    return () => {
      active = false;
      dispatch(clearSuccessionKeyJobs());
      dispatch(clearSuccessionSettings());
    };
    // Query string is applied once when the list opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer
      {...rest}
      location={location}
      navigate={navigate}
      dispatch={dispatch}
      accessControl={accessControl}
      translations={translations}
      navigationItems={navigationItems}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoading && (
        <div>
          <div className={classes.stickyHeader}>
            <Typography
              className={classes.description}
              variant="body2"
              component="div"
            >
              {translations.description}
            </Typography>
            <div className={classes.actions}>
              <Filters
                translations={translations.filters}
                selectedFilters={selectedFilters}
                filters={filters}
                onApplyFilters={onApplyFilters}
              />
              <Search
                className={classes.search}
                placeholder={translations.search}
                value={search}
                onChange={onSearch}
              />
            </div>
          </div>
          {hasRows && (
            <GridTable
              className={classes.table}
              translations={translations}
              headers={headers}
              rows={rows}
              hasLoadMore={hasLoadMore}
              onLoadMore={onLoadMore}
            />
          )}
          <NotificationCard
            title={isFiltered ? '' : translations.noResults.title}
            content={
              isFiltered
                ? translations.noSearchResultsMessage
                : translations.noResults.message
            }
            shouldFade={!isLoading && !hasRows}
            shouldFadeOutInstant
          />
        </div>
      )}
    </PageContainer>
  );
};

SuccessionKeyJobsPage.propTypes = {
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  accessControl: PropTypes.object.isRequired,
};

export default memo(SuccessionKeyJobsPage);
